import React, { useState, useCallback } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { 
    Box, Typography, Container, Paper, 
    Grid, Button, Switch, FormControlLabel, 
    Divider, Slider, Card, CardContent, TextField,
    InputAdornment, Alert, Collapse
} from '@mui/material';
import { 
    LockOpen as LockOpenIcon, 
    Security as SecurityIcon, 
    Timer as TimerIcon, 
    VpnKey as VpnKeyIcon,
    Save as SaveIcon,
    VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// --- MAIN COMPONENT ---
const AuthSecuritySettings = () => {
    const theme = useTheme();
    const [saved, setSaved] = useState(false);

    // 1. Password Policy State
    const [passwordPolicy, setPasswordPolicy] = useState({
        minLength: 12,
        requiresUppercase: true,
        requiresNumber: true,
        requiresSpecialChar: false,
        historyCheck: 5, // Number of previous passwords to block
    });

    // 2. MFA State
    const [mfaSettings, setMfaSettings] = useState({
        enabled: true,
        requiredForAdmins: true,
        enforcementDelayDays: 7,
    });

    // 3. Session Management State
    const [sessionSettings, setSessionSettings] = useState({
        idleTimeoutMinutes: 30,
        maxSessionHours: 8,
        enableConcurrentSessions: false,
    });

    // Unified change handler for simple switches/inputs
    const handleToggleChange = (group, key) => {
        const setter = group === 'mfa' ? setMfaSettings : setSessionSettings;
        setter(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handlePolicySliderChange = (key, value) => {
        setPasswordPolicy(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = useCallback(() => {
        console.log("Saving Auth & Security Settings:", { passwordPolicy, mfaSettings, sessionSettings });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
    }, [passwordPolicy, mfaSettings, sessionSettings]);

    // --- SUB-COMPONENTS (Card Layouts) ---

    const PasswordPolicyCard = () => (
        <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LockOpenIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="primary">Password Policy</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <FormControlLabel
                    control={
                        <Switch 
                            checked={passwordPolicy.requiresUppercase} 
                            onChange={() => handlePolicySliderChange('requiresUppercase', !passwordPolicy.requiresUppercase)} 
                            color="secondary"
                        />
                    }
                    label="Require Uppercase Letters"
                    sx={{ mb: 1 }}
                />
                <FormControlLabel
                    control={
                        <Switch 
                            checked={passwordPolicy.requiresNumber} 
                            onChange={() => handlePolicySliderChange('requiresNumber', !passwordPolicy.requiresNumber)} 
                            color="secondary"
                        />
                    }
                    label="Require Numbers (0-9)"
                    sx={{ mb: 1 }}
                />
                <FormControlLabel
                    control={
                        <Switch 
                            checked={passwordPolicy.requiresSpecialChar} 
                            onChange={() => handlePolicySliderChange('requiresSpecialChar', !passwordPolicy.requiresSpecialChar)} 
                            color="secondary"
                        />
                    }
                    label="Require Special Characters (!@#$%...)"
                    sx={{ mb: 3 }}
                />
                
                <Typography variant="body2" gutterBottom color="textSecondary" sx={{ mt: 2 }}>
                    Minimum Password Length: **{passwordPolicy.minLength} characters**
                </Typography>
                <Slider
                    value={passwordPolicy.minLength}
                    onChange={(e, val) => handlePolicySliderChange('minLength', val)}
                    step={1}
                    marks
                    min={8}
                    max={20}
                    valueLabelDisplay="auto"
                    color="primary"
                    sx={{ width: '95%', ml: 1 }}
                />
                
                <Typography variant="body2" gutterBottom color="textSecondary" sx={{ mt: 3 }}>
                    Password History Check (Prev Passwords): **{passwordPolicy.historyCheck}**
                </Typography>
                <Slider
                    value={passwordPolicy.historyCheck}
                    onChange={(e, val) => handlePolicySliderChange('historyCheck', val)}
                    step={1}
                    marks
                    min={0}
                    max={10}
                    valueLabelDisplay="auto"
                    color="primary"
                    sx={{ width: '95%', ml: 1 }}
                />
            </CardContent>
        </Card>
    );

    const MfaCard = () => (
        <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <VerifiedUserIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="success.main">Multi-Factor Authentication (MFA)</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <FormControlLabel
                    control={
                        <Switch 
                            checked={mfaSettings.enabled} 
                            onChange={() => handleToggleChange('mfa', 'enabled')} 
                            color="success"
                        />
                    }
                    label="Enable MFA across the platform"
                    sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
                    Toggles the availability of MFA (TOTP/SMS) for all users.
                </Typography>

                <FormControlLabel
                    control={
                        <Switch 
                            checked={mfaSettings.requiredForAdmins} 
                            onChange={() => handleToggleChange('mfa', 'requiredForAdmins')} 
                            color="error"
                        />
                    }
                    label="MFA Required for System Administrators"
                    sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4, mb: 3 }}>
                    Enforces MFA for all accounts with System Admin or SuperUser roles.
                </Typography>

                <TextField
                    label="Grace Period for New Users (Days)"
                    value={mfaSettings.enforcementDelayDays}
                    onChange={(e) => setMfaSettings(prev => ({ ...prev, enforcementDelayDays: Number(e.target.value) }))}
                    type="number"
                    size="small"
                    variant="outlined"
                    sx={{ maxWidth: 250 }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">Days</InputAdornment>,
                    }}
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                    Number of days a new user has to set up MFA before enforcement begins.
                </Typography>
            </CardContent>
        </Card>
    );

    const SessionCard = () => (
        <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimerIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="info.main">Session Management</Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <TextField
                    label="Idle Timeout Limit"
                    value={sessionSettings.idleTimeoutMinutes}
                    onChange={(e) => setSessionSettings(prev => ({ ...prev, idleTimeoutMinutes: Number(e.target.value) }))}
                    type="number"
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2, maxWidth: 250 }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">Minutes</InputAdornment>,
                    }}
                    helperText="Session will automatically log out after this period of inactivity."
                />

                <TextField
                    label="Maximum Session Duration"
                    value={sessionSettings.maxSessionHours}
                    onChange={(e) => setSessionSettings(prev => ({ ...prev, maxSessionHours: Number(e.target.value) }))}
                    type="number"
                    size="small"
                    variant="outlined"
                    sx={{ mb: 3, maxWidth: 250 }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">Hours</InputAdornment>,
                    }}
                    helperText="Users will be forced to re-authenticate after this time, regardless of activity."
                />
                
                <FormControlLabel
                    control={
                        <Switch 
                            checked={sessionSettings.enableConcurrentSessions} 
                            onChange={() => handleToggleChange('session', 'enableConcurrentSessions')} 
                            color="error"
                        />
                    }
                    label="Allow Multiple Concurrent Sessions per User"
                    sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
                    Turning this OFF forces a user to log out of all other devices when they log in to a new one.
                </Typography>

            </CardContent>
        </Card>
    );


    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 10 }}> {/* Increased mb for fixed save bar */}
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 4
                }}
            >
                <SecurityIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.error.main }} />
                Authentication & Security Settings
            </Typography>

            {/* Success Alert (Functional UX) */}
            <Collapse in={saved} sx={{ mb: 3 }}>
                <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ fontWeight: 600 }}>
                    Security settings updated successfully! Changes will take effect immediately.
                </Alert>
            </Collapse>

            <Grid container spacing={4}>
                <Grid item xs={12} lg={6}>
                    <PasswordPolicyCard />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <MfaCard />
                </Grid>
                <Grid item xs={12}>
                    <SessionCard />
                </Grid>
            </Grid>

            {/* Sticky Save Bar (Pro-level UX) */}
            <Box 
                sx={{ 
                    position: 'fixed', 
                    bottom: 0, 
                    left: 0, 
                    right: 0, 
                    backgroundColor: theme.palette.background.paper, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    zIndex: 1000,
                    py: 2,
                    px: { xs: 2, sm: 4 },
                    display: 'flex',
                    justifyContent: 'flex-end',
                    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                }}
            >
                <Button 
                    variant="contained" 
                    color="primary" 
                    size="large" 
                    onClick={handleSave}
                    startIcon={<SaveIcon />}
                >
                    Save All Security Settings
                </Button>
            </Box>
        </Container>
    );
};

export default AuthSecuritySettings;

